/**
 * StudySync API Utilities
 * Centralized API communication layer with error handling and retry logic
 */

import { API_CONFIG, API_ENDPOINTS, REQUEST_CONFIG } from '../config/api';

// Custom error classes
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public details?: { field: string; message: string }[]
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error {
  constructor(message: string = 'Network error occurred') {
    super(message);
    this.name = 'NetworkError';
  }
}

// Retry configuration
interface RetryConfig {
  attempts: number;
  delay: number;
  backoff: boolean;
}

const defaultRetryConfig: RetryConfig = {
  attempts: API_CONFIG.RETRY_ATTEMPTS,
  delay: API_CONFIG.RETRY_DELAY,
  backoff: true,
};

// Sleep utility for retry delays
const sleep = (ms: number): Promise<void> => 
  new Promise(resolve => setTimeout(resolve, ms));

// Check if error is retryable
const isRetryableError = (error: any): boolean => {
  if (error instanceof NetworkError) return true;
  if (error instanceof ApiError) {
    // Retry on server errors (5xx) but not client errors (4xx)
    return error.status ? error.status >= 500 : false;
  }
  return false;
};

// Core API request function with retry logic
async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {},
  retryConfig: Partial<RetryConfig> = {}
): Promise<T> {
  const config = { ...defaultRetryConfig, ...retryConfig };
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  
  const requestOptions: RequestInit = {
    ...REQUEST_CONFIG,
    ...options,
    headers: {
      ...REQUEST_CONFIG.headers,
      ...options.headers,
    },
  };

  let lastError: Error = new Error('Request failed');
  
  for (let attempt = 1; attempt <= API_CONFIG.RETRY_ATTEMPTS; attempt++) {
    try {
      
      const response = await fetch(url, {
        ...requestOptions,
        signal: new AbortController().signal,
      });
      
      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        if (!response.ok) {
          throw new ApiError(
            `HTTP ${response.status}: ${response.statusText}`,
            response.status
          );
        }
        return response.text() as unknown as T;
      }

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
        const details = Array.isArray(data.details)
          ? data.details.filter((detail: any) => detail &&
            typeof detail.field === 'string' && typeof detail.message === 'string')
          : undefined;
        throw new ApiError(errorMessage, response.status, data.code, details);
      }
      
      return data;

    } catch (error) {
      
      if (error instanceof DOMException && error.name === 'AbortError') {
        lastError = new NetworkError('Request timeout');
      } else if (error instanceof TypeError && error.message.includes('fetch')) {
        lastError = new NetworkError('Network connection failed');
      } else if (error instanceof ApiError || error instanceof NetworkError) {
        lastError = error;
      } else {
        lastError = new NetworkError('Unknown network error');
      }

      // Don't retry on last attempt or non-retryable errors
      if (attempt === config.attempts || !isRetryableError(lastError)) {
        break;
      }

      // Calculate delay with exponential backoff
      const delay = config.backoff 
        ? config.delay * Math.pow(2, attempt - 1)
        : config.delay;
      
      await sleep(delay);
    }
  }

  throw lastError;
}

// HTTP method helpers
export const api = {
  get: <T = any>(endpoint: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, data?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T = any>(endpoint: string, data?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T = any>(endpoint: string, data?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T = any>(endpoint: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};

// Authenticated request helper
export const authenticatedApi = {
  get: <T = any>(endpoint: string, token: string, options?: RequestInit) =>
    api.get<T>(endpoint, {
      ...options,
      headers: {
        ...options?.headers,
        Authorization: `Bearer ${token}`,
      },
    }),

  post: <T = any>(endpoint: string, data?: any, token?: string, options?: RequestInit) =>
    api.post<T>(endpoint, data, {
      ...options,
      headers: {
        ...options?.headers,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }),

  // Use for non-idempotent operations such as sending an invitation. A lost
  // response must not cause the browser to repeat a request that may already
  // have delivered an email.
  postOnce: <T = any>(endpoint: string, data?: any, token?: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      headers: {
        ...options?.headers,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }, { attempts: 1 }),

  put: <T = any>(endpoint: string, data?: any, token?: string, options?: RequestInit) => {
    const response = api.put<T>(endpoint, data, {
      ...options,
      headers: {
        ...options?.headers,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    return response;
  },

  delete: <T = any>(endpoint: string, token: string, options?: RequestInit) =>
    api.delete<T>(endpoint, {
      ...options,
      headers: {
        ...options?.headers,
        Authorization: `Bearer ${token}`,
      },
    }),
};

// Health check utility
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    await api.get(API_ENDPOINTS.HEALTH);
    return true;
  } catch (error) {
    return false;
  }
};

// Error handling utilities
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof NetworkError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
};

export const isNetworkError = (error: unknown): boolean => {
  return error instanceof NetworkError || 
         (error instanceof Error && error.message.includes('fetch'));
};

export const isApiError = (error: unknown): boolean => {
  return error instanceof ApiError;
};

export default api;
